import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ScorecardNavigation from './ScorecardNavigation';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import ChallengeCard from './ChallengeCard';
import { fetchChallengesForLocation } from './RestaurantBackend';
import type { Challenge } from './challengeTypes';

const normalizeRole = (raw: string | undefined | null): string | null => {
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const TeamChallenges: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const role = useMemo(
    () => normalizeRole(user?.managementUser?.role),
    [user?.managementUser?.role],
  );
  const userLocationId = user?.managementUser?.location_id ?? null;
  // `?probe=…` is forwarded so fake-mode DummyDataService can swap the canned
  // payload (forward-compat / newer-schema probes) without touching the backend.
  const probe = searchParams.get('probe');
  const probeQuery = probe ? `?probe=${encodeURIComponent(probe)}` : '';

  useEffect(() => {
    let cancelled = false;

    if (!userLocationId) {
      setChallenges([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const list = await fetchChallengesForLocation(userLocationId, probeQuery);
        if (!cancelled) setChallenges(list.filter(c => c.is_active));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load challenges');
          setChallenges([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userLocationId, probeQuery]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <main className="mx-auto max-w-2xl px-4 py-6">
        <div className="mb-4">
          <h1 className="font-display text-2xl font-semibold text-ink">Team Challenges</h1>
          <p className="mt-1 text-sm text-inkMuted">
            Active team-wide goals for your location
          </p>
        </div>

        {!userLocationId ? (
          <div className="rounded-lg border border-softBorder bg-white p-6 text-center text-sm text-inkMuted">
            No location associated with this account.
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <ErrorMessage message={error} />
        ) : challenges.length === 0 ? (
          <div className="rounded-lg border border-softBorder bg-white p-6 text-center text-sm text-inkMuted">
            No active challenges
            {role === 'Manager' || role === 'Corporate' ? ' for your location.' : '.'}
          </div>
        ) : (
          // Phase-1: show only the first active challenge (ADR-0001 OQ-3).
          <ChallengeCard status={challenges[0].status} />
        )}
      </main>
    </div>
  );
};

export default TeamChallenges;
